import React, { memo, useMemo } from 'react';
import { BiChevronLeft, BiChevronRight, BiFirstPage, BiLastPage } from 'react-icons/bi';
import type { IconBaseProps, IconType } from 'react-icons';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import styles from './ResponsiveTablePagination.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack, useMediaQuery, useTheme } from '@mui/material';

export type ResponsiveTablePaginationProps = {
    loading?: boolean;
    currentPageNumber: number;
    onChangePage: (pageNumber: number) => void;
    tableSize: number;
    pageSize: number;
    paginationDisplayMaxPages?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
    };
};

type PaginationActionProps = {
    component: IconType;
    disabled?: boolean;
} & IconBaseProps;
export function PaginationAction({component: Component, disabled, onClick, ...restProps}: PaginationActionProps) {
    return (
        <Component
            className={`${styles['page-icon']} ${disabled ? styles.disabled : ''} ${restProps.className  || ''}`}
            onClick={disabled ? undefined: onClick}
            {...restProps}
        />
    );
}

export const ResponsiveTablePagination = memo(
    ({
        currentPageNumber,
        pageSize,
        tableSize,
        onChangePage,
        loading,
        paginationDisplayMaxPages = {
            xs: 2,
            sm: 3,
            md: 4,
            lg: 5,
            xl: 6,
        },
    }: ResponsiveTablePaginationProps) => {
        const theme = useTheme();
        const xs = useMediaQuery(theme.breakpoints.down('sm'));
        const sm = useMediaQuery(theme.breakpoints.up('sm'));
        const md = useMediaQuery(theme.breakpoints.up('md'));
        const lg = useMediaQuery(theme.breakpoints.up('lg'));
        const xl = useMediaQuery(theme.breakpoints.up('xl'));
        function getLastPageNumber(): number {
            return Math.ceil(tableSize / pageSize);
        }

        const pageNumbers = useMemo(() => {
            let displayMaxPages = Number.MAX_SAFE_INTEGER;
            if (paginationDisplayMaxPages) {
                if (xl) {
                    displayMaxPages =
                        paginationDisplayMaxPages.xl ||
                        paginationDisplayMaxPages.lg ||
                        paginationDisplayMaxPages.md ||
                        paginationDisplayMaxPages.sm ||
                        paginationDisplayMaxPages.xs ||
                        Number.MAX_SAFE_INTEGER;
                } else if (lg) {
                    displayMaxPages =
                        paginationDisplayMaxPages.lg ||
                        paginationDisplayMaxPages.md ||
                        paginationDisplayMaxPages.sm ||
                        paginationDisplayMaxPages.xs ||
                        Number.MAX_SAFE_INTEGER;
                } else if (md) {
                    displayMaxPages =
                        paginationDisplayMaxPages.md ||
                        paginationDisplayMaxPages.sm ||
                        paginationDisplayMaxPages.xs ||
                        Number.MAX_SAFE_INTEGER;
                } else if (sm) {
                    displayMaxPages =
                        paginationDisplayMaxPages.sm ||
                        paginationDisplayMaxPages.xs ||
                        Number.MAX_SAFE_INTEGER;
                } else if (xs && paginationDisplayMaxPages.xs) {
                    displayMaxPages = paginationDisplayMaxPages.xs || Number.MAX_SAFE_INTEGER;
                }
            }
            const pagesHalf = Math.ceil(displayMaxPages / 2);
            const pageCount = Math.min(getLastPageNumber(), displayMaxPages);
            const startFrom = Math.max(1, Math.min(currentPageNumber + pagesHalf, getLastPageNumber() + 1) - pageCount);
            return Array.from({ length: pageCount }, (_, i) => i + startFrom);
        }, [pageSize, tableSize, currentPageNumber]);

        return (
            <Stack direction="row" spacing={2} alignItems="center" className={styles['pagination']}>
                <Stack flex={1} direction="row" alignItems="center" columnGap={0.5}>
                    <PaginationAction
                        component={BiFirstPage}
                        onClick={() => onChangePage(1)}
                        disabled={getLastPageNumber() === 0 || currentPageNumber === 1 || loading}
                    />
                    <PaginationAction
                        component={BiChevronLeft}
                        onClick={() => onChangePage(currentPageNumber - 1)}
                        disabled={getLastPageNumber() === 0 || currentPageNumber === 1 || loading}
                    />
                    {pageNumbers.map((pageNumber) => (
                        <span
                            className={`${styles['page-number']}
                            ${currentPageNumber === pageNumber ? styles['current-page'] : ''}
                            ${loading ? styles.disabled : ''}`}
                            key={pageNumber}
                            onClick={() => onChangePage(pageNumber)}
                        >
                            {pageNumber}
                        </span>
                    ))}
                    <PaginationAction
                        component={BiChevronRight}
                        onClick={() => onChangePage(currentPageNumber + 1)}
                        disabled={getLastPageNumber() === 0 ||
                            currentPageNumber === getLastPageNumber() || loading}
                    />
                    <PaginationAction
                        component={BiLastPage}
                        onClick={() => onChangePage(getLastPageNumber())}
                        disabled={getLastPageNumber() === 0 || currentPageNumber === getLastPageNumber() || loading}
                    />
                    {loading && (
                        <FontAwesomeIcon
                            icon={faCircleNotch}
                            aria-label="Currently Loading Content"
                            size="2x"
                        />
                    )}
                </Stack>
                <Stack direction="row" alignItems="center">
                    <span className={styles['page-info']}>
                        {getLastPageNumber() === 0 ? 0 : currentPageNumber} of {getLastPageNumber()}{' '}
                        pages ({tableSize} items)
                    </span>
                </Stack>
            </Stack>
        );
    }
);
